import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Globe, Moon, Sun, Menu, X, Settings, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function TopControls() {
  const { lang, setLang, theme, setTheme } = useApp();
  return (
    <div className="flex items-center gap-2">
      <button data-testid="lang-toggle" onClick={() => setLang(lang === "it" ? "en" : "it")}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-border rounded-md hover:bg-accent transition-colors">
        <Globe className="w-3.5 h-3.5" strokeWidth={2} /> {lang}
      </button>
      <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        className="p-2 border border-border rounded-md hover:bg-accent transition-colors">
        {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={2} /> : <Moon className="w-4 h-4" strokeWidth={2} />}
      </button>
    </div>
  );
}

export function Brand({ onClick }) {
  return (
    <div data-testid="brand-logo" onClick={onClick} className="flex items-center gap-2 cursor-pointer select-none">
      <img src="/logo.png" alt="Revenue AI" className="w-9 h-9 object-contain" draggable="false" />
      <span className="font-serif text-xl font-bold tracking-tight">Revenue<span className="text-primary"> AI</span></span>
    </div>
  );
}

export default function Nav() {
  const { user, logout, t, lang, stopImpersonation } = useApp();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const sub = user?.subscription;
  const impersonating = user?.impersonating;
  const needsPay = user && user.role === "owner" &&
    ["pending_payment", "payment_failed", "expired", "none"].includes(sub?.status) &&
    !["/subscription", "/payment/success"].includes(window.location.pathname);

  const items = user ? [
    { to: "/dashboard", testid: "nav-dashboard", label: t("dashboard"), show: true },
    { to: "/my-property", testid: "nav-my-property", label: lang === "it" ? "La mia struttura" : "My property", show: true },
    { to: "/quick-quote", testid: "nav-quick-quote", label: lang === "it" ? "Preventivo al volo" : "Quick quote", show: true },
    { to: "/rooms", testid: "nav-rooms", label: lang === "it" ? "Camere" : "Rooms", show: true },
    { to: "/competitors", testid: "nav-competitors", label: lang === "it" ? "Competitor" : "Competitors", show: true,
      submenu: [{ to: "/competitors/period", testid: "nav-competitors-period", label: lang === "it" ? "Analizza periodo specifico" : "Analyze specific period" }] },
    { to: "/market", testid: "nav-market", label: lang === "it" ? "Analisi Mercato" : "Market analysis", show: true },
    { to: "/decisions", testid: "nav-decisions", label: lang === "it" ? "Storico decisioni" : "Decisions", show: true },
    { to: "/destination", testid: "nav-destination", label: lang === "it" ? "Destinazione" : "Destination", show: true },
  ].filter((i) => i.show) : [];

  const settingsItems = user ? [
    { to: "/costs", testid: "nav-costs", label: lang === "it" ? "Costi" : "Costs", show: true },
    { to: "/import", testid: "nav-import", label: lang === "it" ? "Importa dati" : "Import data", show: true },
    { to: "/guide", testid: "nav-guides", label: lang === "it" ? "Guide" : "Guides", show: true },
    { to: "/subscription", testid: "nav-subscription", label: lang === "it" ? "Abbonamento" : "Subscription", show: user.role !== "revenue_manager" && user.role !== "staff" },
    { to: "/admin", testid: "nav-admin", label: "Admin", show: user.role === "admin" },
  ].filter((i) => i.show) : [];

  const go = (to) => { setMobileOpen(false); setSettingsOpen(false); nav(to); };

  return (
    <nav className="sticky top-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Brand onClick={() => nav(user ? "/dashboard" : "/")} />
          {user && (
            <div className="hidden lg:flex items-center gap-1">
              {items.map((i) => (
                i.submenu ? (
                  <div key={i.to} className="relative" onMouseEnter={() => setOpenSub(i.to)} onMouseLeave={() => setOpenSub(null)}>
                    <button data-testid={i.testid} onClick={() => go(i.to)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent transition-colors">
                      {i.label}<ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    {openSub === i.to && (
                      <div data-testid={`${i.testid}-menu`} className="absolute left-0 mt-1 w-56 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                        {i.submenu.map((s) => (
                          <button key={s.to} data-testid={`dd-${s.testid}`} onClick={() => go(s.to)}
                            className="w-full text-left px-4 py-2 text-sm font-medium hover:bg-accent transition-colors">{s.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button key={i.to} data-testid={i.testid} onClick={() => go(i.to)}
                    className="px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent transition-colors">{i.label}</button>
                )
              ))}
              <div className="relative" onMouseLeave={() => setSettingsOpen(false)}>
                <button data-testid="nav-settings" onClick={() => setSettingsOpen((v) => !v)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md hover:bg-accent transition-colors">
                  <Settings className="w-4 h-4" />{lang === "it" ? "Impostazioni" : "Settings"}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
                </button>
                {settingsOpen && (
                  <div data-testid="nav-settings-menu" className="absolute right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                    {settingsItems.map((i) => (
                      <button key={i.to} data-testid={`dd-${i.testid}`} onClick={() => go(i.to)}
                        className="w-full text-left px-4 py-2 text-sm font-medium hover:bg-accent transition-colors">{i.label}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <TopControls />
          {user ? (
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={logout}>{t("logout")}</Button>
          ) : (
            <Button data-testid="nav-login-btn" size="sm" onClick={() => nav("/login")}>{t("login")}</Button>
          )}
          {user && (
            <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 border border-border rounded-md hover:bg-accent transition-colors" aria-label="Menu">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>
      </div>

      {user && mobileOpen && (
        <div data-testid="nav-mobile-panel" className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-2 gap-1">
            {items.map((i) => (
              <React.Fragment key={i.to}>
                <button data-testid={`m-${i.testid}`} onClick={() => go(i.to)}
                  className="text-left px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent transition-colors">{i.label}</button>
                {i.submenu && i.submenu.map((s) => (
                  <button key={s.to} data-testid={`m-${s.testid}`} onClick={() => go(s.to)}
                    className="text-left px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent transition-colors text-primary">↳ {s.label}</button>
                ))}
              </React.Fragment>
            ))}
            <div className="col-span-2 mt-2 mb-1 px-3 text-[11px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1.5"><Settings className="w-3.5 h-3.5" />{lang === "it" ? "Impostazioni" : "Settings"}</div>
            {settingsItems.map((i) => (
              <button key={i.to} data-testid={`m-${i.testid}`} onClick={() => go(i.to)}
                className="text-left px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent transition-colors">{i.label}</button>
            ))}
          </div>
        </div>
      )}
      {impersonating && (
        <div data-testid="impersonation-banner" className="bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3 flex-wrap text-sm">
            <span className="font-medium">
              {lang === "it"
                ? `Stai usando l'account di ${user?.email} · admin: ${impersonating.email}`
                : `You are viewing ${user?.email}'s account · admin: ${impersonating.email}`}
            </span>
            <Button size="sm" variant="secondary" data-testid="stop-impersonation-btn"
              onClick={async () => { try { await stopImpersonation(); } catch {} }}>
              {lang === "it" ? "Torna al tuo account" : "Return to your account"}
            </Button>
          </div>
        </div>
      )}
      {needsPay && (
        <div data-testid="subscription-banner" className="bg-amber-500/15 border-t border-amber-500/40 text-amber-800 dark:text-amber-300">
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3 flex-wrap text-sm">
            <span className="font-medium">
              {sub?.status === "payment_failed"
                ? (lang === "it" ? "Il tuo ultimo pagamento non è andato a buon fine. Riattiva l'account." : "Your last payment failed. Reactivate your account.")
                : (lang === "it" ? "Attiva il tuo abbonamento per sbloccare tutte le funzioni di Revenue AI." : "Activate your subscription to unlock all Revenue AI features.")}
            </span>
            <Button size="sm" data-testid="banner-subscribe-btn" onClick={() => nav("/subscription")}>
              {lang === "it" ? "Abbonati ora" : "Subscribe now"}
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}
